import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

/**
 * "Saved from <Name> →" credit line for recipes saved from someone else's
 * Wellby.
 *
 * - Links to the original creator's PROFILE page (their Wellby); falls back
 *   to plain text when the account/username is gone. The profile link works
 *   even when the original recipe itself was deleted or made private.
 * - Avatar always shows: photo when available, initial-on-blush otherwise.
 * - "Saved from" stays brown; the underlined blush name is the link signal.
 */
const SaveCreditLine = ({ recipe, placement = 'card' }) => {
  if (!recipe || !recipe.saved_from_user_id || !recipe.saved_from_name) return null;

  const linked = !!recipe.saved_from_username;
  const placementClassName = placement === 'detail'
    ? 'mt-1 text-base'
    : `${recipe.categories?.length ? 'mt-6' : 'mt-3'} text-sm`;

  const inner = (
    <>
      {recipe.saved_from_photo_url ? (
        <img
          src={recipe.saved_from_photo_url}
          alt=""
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        /* Always show an avatar: brown initial on blush when no photo */
        <span
          aria-hidden="true"
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#AE8890',
            color: '#51120A',
            fontSize: '11px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {recipe.saved_from_name.charAt(0).toUpperCase()}
        </span>
      )}
      <span style={{ textDecoration: 'none' }}>
        <span style={{ color: '#51120A', textDecoration: 'none' }}>Saved from </span>
        {linked ? (
          <RouterLink
            to={`/${recipe.saved_from_username}`}
            onClick={(e) => e.stopPropagation()}
            className="text-link"
          >
            {recipe.saved_from_name}
          </RouterLink>
        ) : (
          <span>{recipe.saved_from_name}</span>
        )}
      </span>
    </>
  );

  // Same brown + size as the "Originally by ..." creator line (size comes
  // from the caller's className).
  const baseStyle = {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    textDecoration: 'none',
  };

  if (linked) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${placementClassName}`}
        style={{ ...baseStyle, color: '#51120A' }}
        data-testid={`save-credit-${recipe.id}`}
      >
        {inner}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${placementClassName}`}
      style={{ ...baseStyle, color: '#51120A' }}
      data-testid={`save-credit-${recipe.id}`}
    >
      {inner}
    </span>
  );
};

export default SaveCreditLine;
